import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Reviews", href: "#reviews", testid: "nav-link-reviews" },
  { label: "Results", href: "#results", testid: "nav-link-results" },
  { label: "Process", href: "#process", testid: "nav-link-process" },
  { label: "Areas", href: "#service-area", testid: "nav-link-areas" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onGallery = location.pathname === "/before-after";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(href), 500);
    } else {
      scrollToId(href);
    }
  };

  const goGallery = () => {
    setOpen(false);
    if (onGallery) {
      window.__lenis?.scrollTo(0, { duration: 1 });
    } else {
      navigate("/before-after");
    }
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-500 ${
          scrolled || onGallery
            ? "border-[#1c4431]/15 bg-[#f2ecdd]/90 backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
          <button
            data-testid="nav-brand"
            onClick={() => go("#top")}
            className="flex items-center gap-3 text-left"
          >
            <img
              src="/logo.jpg"
              alt="Pratt's Hydroseeding & Excavating logo"
              className="h-11 w-11 rounded-md object-cover ring-1 ring-[#1c4431]/25"
            />
            <span className="leading-tight">
              <span className="block font-display text-sm font-bold uppercase tracking-wide text-[#1a2e22]">
                Pratt's
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#2e7d4f]">
                Hydroseeding & Excavating
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={l.testid}
                onClick={() => go(l.href)}
                className="font-mono text-xs uppercase tracking-[0.2em] text-[#41543f] transition-colors duration-300 hover:text-[#1f5c3d]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-link-beforeafter"
              onClick={goGallery}
              className={`font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-[#1f5c3d] ${
                onGallery ? "font-bold text-[#1f5c3d]" : "text-[#41543f]"
              }`}
            >
              Before / After
            </button>
            <a
              data-testid="nav-call-button"
              href="tel:+14237482012"
              className="flex items-center gap-2 rounded-full bg-[#1f5c3d] px-5 py-2.5 font-mono text-xs font-bold tracking-widest text-[#f6f1e3] transition-colors duration-300 hover:bg-[#2a7452]"
            >
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-[#7fce9e]" />
              423-748-2012
            </a>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#1a2e22] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-x-0 top-0 z-40 border-b border-[#1c4431]/15 bg-[#f2ecdd]/97 pt-24 pb-10 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex flex-col items-center gap-7">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-${l.testid}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i }}
                  onClick={() => go(l.href)}
                  className="font-display text-2xl font-bold uppercase tracking-wide text-[#1a2e22]"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-nav-link-beforeafter"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * LINKS.length }}
                onClick={goGallery}
                className="font-display text-2xl font-bold uppercase tracking-wide text-[#1f5c3d]"
              >
                Before / After
              </motion.button>
              <a
                data-testid="mobile-nav-call-button"
                href="tel:+14237482012"
                className="mt-2 flex items-center gap-2 rounded-full bg-[#1f5c3d] px-8 py-3 font-mono text-sm font-bold tracking-widest text-[#f6f1e3]"
              >
                <Phone className="h-4 w-4" />
                423-748-2012
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
